import { mapStore } from "../stores/map.store";
import { hooksStore } from "../stores/hooks.store";

import {
  vratVrstvyMapyPodlaTypu,
  vratHtmlKodFarbyPodlaTypu,
  vratKodFarbyPodlaTypu,
} from "../helpers/config";
import { vratGeojsonData } from "../api/geojson";

import bbox from "@turf/bbox";
import center from "@turf/center";
import { get as get_store } from "svelte/store";
import get from "get-value";
import set from "set-value";

hooksStore.addAction("zobrazDataNaMape", "map.js", zobrazDataNaMape);
hooksStore.addAction("odstranVrstvuZMapy", "map.js", odstranVrstvuZMapy);

/**
 * Vratenie objektu mapy z maplibre
 * @returns {object}  Objekt maplibre
 */
export function getMap() {
  const map = get_store(mapStore);
  if (map && get(map, "maplibre", false)) {
    return map.maplibre.getMap();
  }
}

/**
 * Zobrazenie zvolenych dat na mape
 * @param {object} data Data objektu, ktory sa ma zobrazit na mape
 * @param {boolean} doPointTo Centrovanie mapy na zobrazeny objekt
 * @returns {boolean}
 */
export async function zobrazDataNaMape(data, doPointTo = true) {
  const { typ, id, hover } = nastavPremenneZDatZdroja(data);
  const map = getMap();

  // vytvorim si ID pre zdroj mapboxu. Musi to byt jedinecne
  const source_id = vygenerujIdZdroja(typ, id);
  if (!source_id) {
    return false;
  }
  // z konfigu si vytiahnem data o vrstve daneho typu
  // tych vrstiev moze byt viac. Napr lokalita ma oramovanie a vyfarbenie
  const vrstvy = vratVrstvyMapyPodlaTypu(typ);

  if (vrstvy) {
    // ak uz taky zdroj mam, tak ho nemozem znova pridava
    // ale mozem ho znova nacentrovat
    if (sourceExists(source_id) !== false) {
      // ziskam si data daneho zdroja priamo z mapy
      // a nemusim to ziskavat cez api
      const source = map.getSource(source_id);
      if (doPointTo) {
        pointTo(source._data, vrstvy[0].typ_vrstvy);
      }
      return false;
    }

    let sourceData = await hooksStore.applyFilters(
      "zobrazDataNaMape_vratGeojsonData",
      false,
      typ,
      id
    );

    // z api vratim geojson data
    if (!sourceData) {
      sourceData = await vratGeojsonData(typ, id);
    }
    if (!sourceData || sourceExists(source_id) !== false) {
      return false;
    }

    sourceData = hooksStore.applyFilters(
      "zobrazDataNaMape_upravSourceData",
      sourceData,
      typ,
      id
    );

    // vytvorim si zdroj dat
    map.addSource(source_id, {
      type: "geojson",
      data: sourceData,
      generateId: true,
    });

    // pridam vrstvy do zdroja
    pridajVrstvy(typ, id);

    // nacentrujem mapu na dane data na mape
    if (doPointTo) {
      pointTo(sourceData, vrstvy[0].typ_vrstvy);
    }

    if (get(data, "nazov") === undefined) {
      set(data, "nazov", get(sourceData, "features.0.properties.nazov"));
    }

    // ak som nerobil dvojklik
    if (hover === false) {
      hooksStore.doAction(
        "ulozVrstvyDoStavu",
        data,
        source_id,
        sourceData,
        vrstvy[0].typ_vrstvy
      );
    }
  }

  return true;
}

/**
 * Vratenie konkretnych dat z objektu
 * @param {object} data Objekt dat zdroja
 * @returns {object}
 */
export function nastavPremenneZDatZdroja(data) {
  if (data === null || data !== Object(data)) {
    return false;
  }

  // zistim si typ dat ktore potrebujem zonbrazit
  const typ = data.gistyp || data.typ || data.type;
  // potrebujem ID dat, aby som podla toho zavolal api
  // FIX: parcely mozu mat ID s pomlckou!
  // const id = /^(\d+-?)+\d+$/.test(data.id) ? data.id : false;
  const id = data.id.toString();
  // ak som spravil dvojklik a v zobrazenom popupe
  // prechadzam jednotlivymi vrstvami
  const hover = data.hover === true || false;

  if (!typ || !id) {
    return false;
  }

  return { typ, id, hover };
}

/**
 * Vygenerovanie ID zdroja
 * @param {string} typ
 * @param {string} id
 * @returns {string}
 */
export function vygenerujIdZdroja(typ, id) {
  id = id.toString();
  if (!typ || !id) {
    return false;
  }
  let source_id = hooksStore.applyFilters(
    "source_id",
    `sm_${typ}_${id}`,
    typ,
    id
  );
  return source_id;
}

/**
 * Kontrola, ci zdroj v mape uz existuje
 * @param {string} id ID zdroja
 * @returns {boolean}
 */
export function sourceExists(id) {
  const map = getMap();

  if (typeof map === "undefined" || typeof id === "undefined" || id === "") {
    return false;
  }

  const source = map.getSource(id);

  return source ? true : false;
}

/**
 * Nacentrovanie a nazoomovanie na konkretny zdroj na mape
 * @param {object} sourceData Data zdroja
 * @param {string} typ_vrstvy Typ vrstvy
 */
export function pointTo(sourceData, typ_vrstvy) {
  const map = getMap();
  // ak je typ vrstvy sumbol, cize nejaky point, tak musim inak nacentrovat objekt
  // lebo funkcia fitBounds by bod nazoomoval az moc
  if (typ_vrstvy === "symbol") {
    const point = center(sourceData);
    map.flyTo({
      center: point.geometry.coordinates,
      zoom: 17,
    });
  } else {
    const bounds = bbox(sourceData);
    map.fitBounds(bounds, { padding: 100 });
  }
}

/**
 * Nacentrovanie a nazoomovanie na konkretny zdroj na mape
 * @param {object} sourceData Data zdroja
 * @param {string} typ_vrstvy Typ vrstvy
 */
export function panTo(sourceData, typ_vrstvy) {
  const map = getMap();

  const point = center(sourceData);
  map.panTo(point.geometry.coordinates);
}

/**
 * Pridanie vrstiev do zdroja na mape
 * @param {string} typ Typ vrstvy
 * @param {string} id ID vrstvy
 * @param {boolean} isPopup Nazoomovanie na danu vrztvu
 * @returns {undefined}
 */
export function pridajVrstvy(
  typ,
  id,
  isPopup = false,
  source_id = false,
  typRaw,
  inc = 0
) {
  source_id = source_id || vygenerujIdZdroja(typ, id);
  if (!source_id) {
    return false;
  }
  const map = getMap();

  // z konfigu si vytiahnem data o vrstve daneho typu
  // tych vrstiev moze byt viac. Napr lokalita ma oramovanie a vyfarbenie
  const vrstvy = vratVrstvyMapyPodlaTypu(typ);

  // idem po jednej vrstve a samostatne jej nastavim layer
  vrstvy.forEach((vrstva, i) => {
    // vrstva moze mat nastavenia pre paint aj pre layout. Je to podla rozneho typu vrstvy
    for (const v in vrstva.paint) {
      if (typeof vrstva.paint[v] === "string") {
        vrstvy[i].paint[v] = vrstvy[i].paint[v].replace(
          /\%kod_farby\%/,
          `#${vratHtmlKodFarbyPodlaTypu(typ)}`
        );
      }
    }

    for (const v in vrstva.layout) {
      if (typeof vrstva.layout[v] === "string") {
        vrstvy[i].layout[v] = vrstvy[i].layout[v].replace(
          /\%kod_farby\%/,
          `#${vratHtmlKodFarbyPodlaTypu(typ)}`
        );
      }
    }

    // ak nemam v konfigu vetvu pre layout, tak ju vytvorim
    if (vrstva.layout === undefined) {
      vrstva.layout = {};
    }

    // defualtne nastavim visibility pre vsetky vrstvy
    // toto sa pouziva pri hoveri na dvojkliku
    vrstva.layout.visibility = "visible";
    vrstva = hooksStore.applyFilters(
      "upravaNastaveniaVrstvy",
      vrstva,
      typ,
      source_id
    );

    const typ_vrstvy = vrstva.typ_vrstvy;
    // vytvorim layer pre danu vrstvu
    let layerOptions = {
      id: `${source_id}_${inc}_${i}`,
      type: typ_vrstvy,
      source: source_id,
      paint: vrstva.paint || {},
      layout: vrstva.layout || {},
    };
    if (vrstva.filter) {
      layerOptions.filter = vrstva.filter;
    }
    if (typRaw) {
      layerOptions.filter = ["==", "$type", typRaw];
    }
    const l = map.addLayer(layerOptions);
    vrstvyEventy(source_id, i, inc);
  });
}

/**
 * Pridanie eventov na zobrazeny zdroj
 * @param {string} source_id ID zdroja
 * @param {int} i Poradove cislo layeru
 */
export function vrstvyEventy(source_id, i, inc = 0) {
  const map = getMap();
  // Change the cursor to a pointer when the mouse is over the places layer.
  map.on("mouseenter", `${source_id}_${inc}_${i}`, function () {
    map.getCanvas().style.cursor = "pointer";
  });

  // Change it back to a pointer when it leaves.
  map.on("mouseleave", `${source_id}_${inc}_${i}`, function () {
    map.getCanvas().style.cursor = "";
  });

  map.on("click", `${source_id}_${inc}_${i}`, sourceClickEvent);
}

/**
 * Event na klik na zdroj na mape
 * @param {object} e Event
 */
function sourceClickEvent(e) {
  const coordinates = e.features[0].geometry.coordinates.slice();
  let description;
  if (get(e, "features.0.properties.nazov")) {
    const typ =
      e.features[0].properties.gistyp || e.features[0].properties.type;
    description = `<span class="link color__text--${vratKodFarbyPodlaTypu(
      typ
    )}" data-typ="${typ}" data-id="${e.features[0].properties.id || ""}">${
      e.features[0].properties.nazov
    }</span>`;
  } else {
    description =
      e.features[0].properties.description || e.features[0].properties.popis;
  }

  // Ensure that if the map is zoomed out such that multiple
  // copies of the feature are visible, the popup appears
  // over the copy being pointed to.
  while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
    coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
  }

  hooksStore.doAction(
    "zobrazPopUpOkno",
    [e.lngLat.lng, e.lngLat.lat],
    description
  );
}

/**
 * Odstranim vrstvu z mapy aj z pamate a tym padom aj z navigacneho okienka
 * @param source_id
 */
export function odstranVrstvuZMapy(source_id) {
  const map = getMap();
  // prechadzam vsetky layery, nie sources, lebo najprv treba vymazat layer
  map.getStyle().layers.forEach(function (layer) {
    // ak ma layer spravny source, tak ho vymazem
    if (source_id === layer.source) {
      map.off("click", layer.id, sourceClickEvent);
      map.removeLayer(layer.id);
    }
  });
  // ak existuje source ktory mam vymazat
  if (map.getSource(source_id)) {
    map.removeSource(source_id);
    /* // zmazem informaciu aj z pamate stavu uzivatela
      const u = userStore.getUser();
      let stav = get(u.mapa.stav);
      if (get(stav[source_id]) !== undefined) {
        delete stav[source_id];
      }
      userStore.updateUser(u); */
  }
}

/**
 * Funkcia nastavi viditelnost pre konkretny layer a vsetky vrstvy v nom
 * @param {object} map Objekt mapboxu
 * @param {string} typ
 * @param {string} source_id
 * @param {string} visibility "visible" alebo "none"
 */
export function nastavViditelnostVrstvy(typ, source_id, visibility, inc = 0) {
  const map = getMap();
  // z konfigu si vytiahnem data o vrstve daneho typu
  // tych vrstiev moze byt viac. Napr lokalita ma oramovanie a vyfarbenie
  const vrstvy = vratVrstvyMapyPodlaTypu(typ);

  if (vrstvy) {
    // idem po jednej vrstve a samostatne jej nastavim layer
    vrstvy.forEach((vrstva, i) => {
      if (map.getLayer(`${source_id}_${inc}_${i}`)) {
        map.setLayoutProperty(
          `${source_id}_${inc}_${i}`,
          "visibility",
          visibility
        );
      }
    });
  }
}
